import { useContext, useEffect, useState } from 'react';

import {
  createLoginProject,
  deleteLoginProject,
  getBabkaLoginOAuthSettings,
  getLoginProjects
} from '../../api';
import {
  CreateLoginParams,
  LoginProjectData,
  MauStatistic,
  UserStatistic
} from '../../api/dasboard/types';
import { Error as ErrorType } from '../../api/setup/getApiError';
import { useConfig } from '../config';
import { SettingsContext } from './context';
import { ProjectsProps } from './types';

export const useProjects = (): ProjectsProps => {
  const {
    projects,
    setProjects,
    babkaLoginClient,
    setBabkaLoginClient,
    showBabkaLogin,
    setShowBabkaLogin
  } = useContext(SettingsContext);
  const { merchantId, projectId: publisherProjectId } = useConfig();
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const [errorBabka, setErrorBabka] = useState<ErrorType | null>(null);

  useEffect(() => {
    if (projects === null) {
      Promise.allSettled([
        getLoginProjects(merchantId, publisherProjectId),
        getBabkaLoginOAuthSettings(merchantId.toString())
      ])
        .then(([resLogin, resBabka]) => {
          if (resLogin.status === 'fulfilled') {
            setProjects(resLogin.value);
          } else {
            setError(resLogin.reason);
          }
          if (resBabka.status === 'fulfilled') {
            setBabkaLoginClient(resBabka.value.clients);
            setShowBabkaLogin(true);
          } else {
            setShowBabkaLogin(false);
            setErrorBabka(resBabka.reason);
          }
        })
        .finally(() => setFetching(false));
    }
  }, []);

  const updateBabkaLoginClients = async () => {
    getBabkaLoginOAuthSettings(publisherProjectId.toString())
      .then((res) => {
        setBabkaLoginClient(res.clients);
      })
      .catch((err) => {
        setErrorBabka(err);
      });
  };

  const findIndexById = (projectId: string) =>
    projects?.findIndex((project) => project.id === projectId);

  const getProjectById = (projectId: string) =>
    projects?.find((project) => project.id === projectId);

  const createProject = async (project: CreateLoginParams) => {
    const newProjectId = await createLoginProject(merchantId, publisherProjectId, project);
    const newProject: LoginProjectData = {
      ...project,
      id: newProjectId.id,
      login_urls: [],
      game_pages: [],
      publisher_id: merchantId
    };

    if (projects) {
      setProjects([...projects, newProject]);
    } else {
      setProjects([newProject]);
    }

    return newProject;
  };

  const changeProject = (project: LoginProjectData) => {
    if (projects) {
      const index = findIndexById(project.id);

      if (index !== undefined) {
        projects.splice(index, 1, project);
        setProjects([...projects]);
      }
    }
  };

  const updateProjectStatistic = (
    projectId: string,
    data: Required<UserStatistic & MauStatistic>
  ) => {
    let project = getProjectById(projectId);

    if (project) {
      project = {
        ...project,
        ...data
      };
      changeProject(project);
    }

    return project;
  };

  const deleteProject = async (projectId: string) => {
    try {
      const deleteProjectResponse = await deleteLoginProject(projectId);

      if (deleteProjectResponse && projects) {
        const index = findIndexById(projectId);

        if (index !== undefined) {
          projects.splice(index, 1);
          setProjects([...projects]);
        }
      }

      return deleteProjectResponse;
    } catch (e) {
      throw new Error(e);
    }
  };

  return {
    projectsFetching: fetching,
    error,
    errorBabka,
    projects,
    getProjectById,
    createProject,
    updateProjectStatistic,
    changeProject,
    deleteProject,
    babkaLoginClient,
    setBabkaLoginClient,
    showBabkaLogin,
    updateBabkaLoginClients
  };
};
